import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, RedirectCommand, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take } from 'rxjs/operators';

import { HeapService } from '#core/services/heap.service';
import { RestaurantsService } from '#core/services/restaurants.service';
import { ToastService } from '#core/services/toast.service';
import { SessionStore } from '#core/store/session.store';
import { Restaurant } from '#modules/restaurants/models/restaurant';
import { RestaurantStore } from '#modules/restaurants/stores/restaurant.store';

@Injectable()
export class RestaurantResolver {
    private readonly _restaurantsService = inject(RestaurantsService);
    private readonly _heapService = inject(HeapService);
    private readonly _router = inject(Router);
    private readonly _toastService = inject(ToastService);
    private readonly _translateService = inject(TranslateService);

    private readonly _sessionStore = inject(SessionStore);
    private readonly _restaurantStore = inject(RestaurantStore);

    private readonly _isSessionStoreLoaded$ = toObservable(this._sessionStore.isLoaded);

    resolve(route: ActivatedRouteSnapshot): any {
        this._restaurantStore.setLoading();
        const restaurantId = route.params.restaurant_id;

        return this._isSessionStoreLoaded$.pipe(
            filter((isLoaded) => isLoaded),
            take(1),
            switchMap(() => this._restaurantsService.show(restaurantId)),
            take(1),
            map((restaurantResponse) => {
                const restaurant: Restaurant = restaurantResponse.data;
                this._restaurantStore.setRestaurant(restaurant);
                this._restaurantStore.setLoaded();

                const currentUser = this._sessionStore.currentUser();
                const currentUserRestaurants = this._sessionStore.currentUserRestaurants();
                const { name, address } = restaurant;
                this._heapService.addEventProperties({
                    restaurant: `${name}__${address?.formattedAddress}`,
                    userId: currentUser.id,
                    userName: currentUser.name,
                    restaurantId: restaurant._id,
                    userRestaurantsCount: currentUserRestaurants.length,
                    role: currentUser.role,
                    userEmail: currentUser.email,
                    restaurantOrganisation: restaurant.organization?.name,
                    mobileAppSession: false,
                    allBusinesses: false,
                });

                return restaurant;
            }),
            catchError((error: unknown) => {
                const isAccessRevoked = error instanceof HttpErrorResponse && error.status === 403;
                this._restaurantStore.setError(isAccessRevoked ? 'Restaurant access revoked' : 'Restaurant could not be loaded');
                this._toastService.openErrorToast(
                    this._translateService.instant(isAccessRevoked ? 'errors.restaurant.access_revoked' : 'errors.restaurant.load_failed')
                );

                // Keep the restaurant already in the store.
                // Nulling it crashes the views still mounted on the route being left, which read `currentRestaurant()`.
                if (this._canStayOnCurrentPage()) {
                    return EMPTY;
                }
                // Never resolve `null` instead: the route would activate without a restaurant.
                return of(new RedirectCommand(this._router.parseUrl('/restaurants/list')));
            }),
            finalize(() => {
                // A response or an error has already set the status, so only a navigation cancelled while loading gets here with 'loading'.
                // It leaves the restaurant already in the store, which is still loaded; without a restaurant there is nothing to put back.
                if (this._restaurantStore.isLoading() && this._restaurantStore.restaurant()) {
                    this._restaurantStore.setLoaded();
                }
            })
        );
    }

    /**
     * Cancelling the navigation leaves the user on the page they are already on, which beats sending them to the restaurant list.
     * AuthGuard stores the URL it bounced and the login page replays it, so a deep link into a lost restaurant fails while `router.url` is still `/auth/login`.
     * Cancelling there would leave the user on the login form while already logged in, so that case redirects instead.
     */
    private _canStayOnCurrentPage(): boolean {
        return this._router.navigated && !this._router.url.startsWith('/auth');
    }
}
